import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';

import CategoryIconWidget from '~/components/Common/Forms/widgets/CategoryIconWidget';
import Spinner from '~/components/Common/Spinner';
import { CruiseInclusion, CruiseInclusionItem } from '~/components/Cruises/pages/Inclusions/types';

import useAppNavigation from '~/hooks/useAppNavigation';

import inclusionsService from '~/services/cruises/InclusionsService';

const TYPES: { [key: string]: CruiseInclusionItem['type'] } = {
  FREE_TEXT: 'FREE_TEXT',
  ONBOARD_CREDIT: 'ONBOARD_CREDIT',
};

export const ONBOARD_CREDIT_ICON = 'dollar_sign_in_circle';

const INITIAL_FORM_STATE: Partial<CruiseInclusionItem> = {
  description: '',
  type: TYPES.ONBOARD_CREDIT,
  creditCurrency: undefined,
  creditAmount: 0,
  icon: ONBOARD_CREDIT_ICON,
  isHighlighted: false,
};

const buildInitialFormState = (inclusionType: CruiseInclusion['type']): Partial<CruiseInclusionItem> => {
  if (inclusionType === 'EVERGREEN') {
    return {
      ...INITIAL_FORM_STATE,
      type: TYPES.FREE_TEXT,
      icon: undefined,
    };
  }

  return INITIAL_FORM_STATE;
};

type Props = {
  inclusionId: string;
  inclusionItem?: CruiseInclusionItem;
  inclusionType: CruiseInclusion['type'];
  onSave: () => void;
};

function InclusionItemForm({ inclusionId, inclusionItem, inclusionType, onSave }: Props) {
  const { push } = useAppNavigation();
  const [formValues, setFormValues] = useState<Partial<CruiseInclusionItem>>(() => buildInitialFormState(inclusionType));
  const [status, setStatus] = useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const isEvergreenInclusion = inclusionType === 'EVERGREEN';
  const { control, setValue } = useForm({
    defaultValues: {
      icon: '',
    },
  });

  const handleChangeField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value ?? undefined,
      });
    },
    [formValues],
  );

  const handleChangeNumberField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      setFormValues({
        ...formValues,
        [event.target.name]: parseFloat(event.target.value),
      });
    },
    [formValues],
  );

  const handleSelectChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        setFormValues({
          ...formValues,
          [event.target.name]: event.target.value,
        });
      }
    },
    [formValues],
  );

  const handleChangeIcon = useCallback(
    (name: string, value: unknown, config?: object) => {
      setValue(name as 'icon', value as string, config);
      setFormValues({
        ...formValues,
        icon: (value as string) || undefined,
      });
    },
    [formValues, setValue],
  );

  const handleChangeTab = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();

      const type = event.target.value as CruiseInclusionItem['type'];
      const icon = type === TYPES.ONBOARD_CREDIT ? ONBOARD_CREDIT_ICON : undefined;

      setValue('icon', icon ?? '');
      setFormValues({
        ...buildInitialFormState(inclusionType),
        [event.target.name]: type,
        icon,
      });
    },
    [inclusionType, setValue],
  );

  const handleSubmit = useCallback(async () => {
    const isEditing = !!inclusionItem;
    setStatus('loading');

    try {
      const payload = {
        ...formValues,
        icon: formValues.type === TYPES.ONBOARD_CREDIT ? ONBOARD_CREDIT_ICON : formValues.icon,
      };

      if (isEditing) {
        await inclusionsService.updateItem(inclusionId, inclusionItem.id, payload);
      } else {
        await inclusionsService.createItem(inclusionId, payload);
      }

      setStatus('success');
      enqueueSnackbar('Inclusion Item saved with success', { variant: 'success' });
      onSave();
      push(`/cruises/inclusions/edit/${inclusionId}/items`);
    } catch (error) {
      console.error(error);
      setStatus('error');
      enqueueSnackbar('Error saving Inclusion Item', { variant: 'error' });
    }
  }, [formValues, push, enqueueSnackbar, inclusionId, inclusionItem, onSave]);

  const handleCancel = useCallback(() => {
    push(`/cruises/inclusions/edit/${inclusionId}/items`);
  }, [push, inclusionId]);

  const isSaveEnabled = useMemo(() => {
    const isLoading = status === 'loading';
    if (formValues.type === TYPES.ONBOARD_CREDIT) {
      return !isLoading && formValues.creditCurrency && formValues.creditAmount;
    }

    return !isLoading && formValues.description;
  }, [formValues, status]);

  useEffect(() => {
    if (inclusionItem) {
      setFormValues({
        type: inclusionItem.type,
        description: inclusionItem.description,
        creditCurrency: inclusionItem.creditCurrency,
        creditAmount: inclusionItem.creditAmount,
        icon: inclusionItem.icon ?? (inclusionItem.type === TYPES.ONBOARD_CREDIT ? ONBOARD_CREDIT_ICON : undefined),
      });
      setValue('icon', inclusionItem.icon ?? '');
    }
  }, [inclusionItem, setValue]);

  return (
    <Box>
      <Box>
        <FormControl>
          <RadioGroup defaultValue={TYPES.FREE_TEXT} name="type" value={formValues.type} onChange={handleChangeTab} row>
            {!isEvergreenInclusion && (
              <FormControlLabel value={TYPES.ONBOARD_CREDIT} control={<Radio />} label="Onboard Credit" />
            )}
            <FormControlLabel value={TYPES.FREE_TEXT} control={<Radio />} label="Free Text" />
          </RadioGroup>
        </FormControl>
      </Box>

      {formValues.type === TYPES.ONBOARD_CREDIT && (
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box mt={2}>
              <FormControl fullWidth>
                <TextField
                  select
                  name="creditCurrency"
                  label="Currency"
                  onChange={handleSelectChange}
                  value={formValues.creditCurrency}
                  key={formValues.creditCurrency}
                >
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </TextField>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={9}>
            <Box mt={2}>
              <TextField
                fullWidth
                type="number"
                name="creditAmount"
                label="Amount"
                onChange={handleChangeNumberField}
                value={formValues.creditAmount}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {formValues.type === TYPES.FREE_TEXT && (
        <>
          <Box mt={2}>
            <CategoryIconWidget control={control} label="Icon" name="icon" setValue={handleChangeIcon} />
          </Box>

          <Box mt={2}>
            <TextField
              required
              fullWidth
              name="description"
              label="Description"
              onChange={handleChangeField}
              inputProps={{ maxLength: 50 }}
              value={formValues.description}
              helperText="The maximum length is 50 characters."
            />
          </Box>
        </>
      )}

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {status === 'loading' && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default InclusionItemForm;
